import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Phone, CheckCircle, HardHat, Wrench, Home as HomeIcon, Droplets, Zap, Mountain } from "lucide-react";
import ScrollReveal from "@/components/ScrollReveal";
import SEOHead from "@/components/SEOHead";
import QuoteForm from "@/components/QuoteForm";
import { seoPages } from "@/data/seoPages";
import { CTA_DEVIS } from "@/data/forms";

const services = [
  { icon: HardHat, title: "Construction", getDesc: (c) => `Construction de maisons, immeubles et bâtiments commerciaux à ${c}. Gros oeuvres, fondations, structure et finitions clés en main.` },
  { icon: Wrench, title: "Rénovation intérieur & extérieur", getDesc: (c) => `Rénovation complète ou partielle à ${c}. Cuisine, salle de bain, façade, isolation — nous transformons votre espace avec soin et précision.` },
  { icon: HomeIcon, title: "Travaux de toiture", getDesc: (c) => `Pose, réparation et isolation de toiture à ${c}. Tuiles, ardoises, toiture plate, zinc — nos couvreurs maîtrisent toutes les techniques.` },
  { icon: Droplets, title: "Plomberie & Chauffage", getDesc: (c) => `Installation et dépannage plomberie et chauffage à ${c}. Chaudières, radiateurs, sanitaires, plancher chauffant — intervention rapide.` },
  { icon: Zap, title: "Électricité", getDesc: (c) => `Mise aux normes, nouvelles installations et dépannage électrique à ${c}. Installations conformes RGIE et sécurisées.` },
  { icon: Mountain, title: "Terrassement", getDesc: (c) => `Travaux de terrassement à ${c}. Nivellement, excavation, drainage, fondations et aménagement extérieur.` },
];

export default function SeoLocalPage({ slug }) {
  const page = seoPages[slug];

  if (!page) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-black text-[#1a1a1a] mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Page introuvable</h1>
          <Link to="/" className="text-[#8B0000] hover:underline">Retour à l'accueil</Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid={`seo-page-${slug}`}>
      <SEOHead
        title={`Travaux de rénovation à ${page.city} | VGI Construct – Devis gratuit sous 48 h`}
        description={`VGI Construct à ${page.city} : rénovation complète, extension, toiture, châssis, isolation, électricité, plomberie, chauffage. Entreprise générale créée en 2018, active en ${page.region}. Devis gratuit – réponse sous 48 h.`}
        path={`/travaux-${slug}`}
      />
      {/* 1. Hero */}
      <section className="py-24 sm:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <p className="uppercase text-xs tracking-[0.2em] font-semibold text-[#8B0000] mb-4">{page.region}</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-[#1a1a1a] mb-6" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              VGI Construct à {page.city}
            </h1>
            <p className="text-[#444] text-xl max-w-2xl mb-8">
              Construction, rénovation et tous travaux à {page.city} et environs
            </p>
            <div className="flex flex-wrap gap-4">
              <a href="tel:+32466407988" className="btn-primary flex items-center gap-2"><Phone size={18} /> +32 466 407 988</a>
              <Link to="/demande-devis" className="btn-secondary flex items-center gap-2">{CTA_DEVIS} <ArrowRight size={16} /></Link>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* 2. Introduction */}
      <section className="py-16 sm:py-24 bg-[#f5f5f5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-8">
              <ScrollReveal>
                <h2 className="text-2xl sm:text-3xl font-bold text-[#1a1a1a] mb-6" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                  Votre entreprise de construction à {page.city}
                </h2>
                <div className="text-[#444] text-base sm:text-lg leading-relaxed space-y-4">
                  {page.intro.split('\n').map((p, i) => <p key={i}>{p}</p>)}
                </div>
              </ScrollReveal>
            </div>
            <div className="lg:col-span-4">
              <ScrollReveal delay={200}>
                <div className="bg-white border border-gray-200 p-6 rounded-sm shadow-sm sticky top-24">
                  <h3 className="text-[#1a1a1a] text-lg font-bold mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Contactez-nous</h3>
                  <a href="tel:+32466407988" className="flex items-center gap-3 text-[#444] hover:text-[#8B0000] transition-colors text-sm mb-3"><Phone size={16} className="text-[#8B0000]" /> +32 466 407 988</a>
                  <p className="text-[#888] text-sm mb-1">Lun–ven : 8h–18h</p>
                  <p className="text-[#888] text-sm mb-4">Urgences dépannage 7j/7</p>
                  <Link to="/demande-devis" className="btn-primary w-full text-center block text-sm">{CTA_DEVIS}</Link>
                </div>
              </ScrollReveal>
            </div>
          </div>
        </div>
      </section>

      {/* 3. Nos 6 services */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <p className="uppercase text-xs tracking-[0.2em] font-semibold text-[#8B0000] mb-4">Nos services</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1a1a1a] mb-12" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Nos 6 services à {page.city}
            </h2>
          </ScrollReveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((svc, i) => (
              <ScrollReveal key={svc.title} delay={i * 100}>
                <div className="hover-card p-6 sm:p-8 h-full group">
                  <svc.icon size={32} className="text-[#8B0000] mb-4 group-hover:scale-110 transition-transform" />
                  <h3 className="text-[#1a1a1a] text-lg font-bold mb-3" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{svc.title}</h3>
                  <p className="text-[#444] text-sm leading-relaxed">{svc.getDesc(page.city)}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* 4. Pourquoi nous choisir */}
      <section className="py-16 sm:py-24 bg-[#f5f5f5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1a1a1a] mb-12" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Pourquoi nous choisir à {page.city}
            </h2>
          </ScrollReveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <ScrollReveal delay={0}>
              <div className="hover-card p-6 h-full">
                <CheckCircle size={24} className="text-[#8B0000] mb-4" />
                <h3 className="text-[#1a1a1a] font-bold mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Intervention rapide</h3>
                <p className="text-[#444] text-sm leading-relaxed">Notre équipe intervient rapidement à {page.city}. Basés à Sint-Pieters-Leeuw, nous sommes à proximité pour tous vos projets urgents ou planifiés.</p>
              </div>
            </ScrollReveal>
            <ScrollReveal delay={150}>
              <div className="hover-card p-6 h-full">
                <CheckCircle size={24} className="text-[#8B0000] mb-4" />
                <h3 className="text-[#1a1a1a] font-bold mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Connaissance du quartier</h3>
                <p className="text-[#444] text-sm leading-relaxed">{page.localKnowledge}</p>
              </div>
            </ScrollReveal>
            <ScrollReveal delay={300}>
              <div className="hover-card p-6 h-full">
                <CheckCircle size={24} className="text-[#8B0000] mb-4" />
                <h3 className="text-[#1a1a1a] font-bold mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Devis gratuit – réponse sous 48 h</h3>
                <p className="text-[#444] text-sm leading-relaxed">Recevez un devis détaillé et transparent sous 48 h. Pas de frais cachés, pas de surprises. Notre engagement : le meilleur rapport qualité-prix à {page.city}.</p>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* 5. Formulaire devis */}
      <section className="py-16 sm:py-24 bg-white border-t border-gray-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1a1a1a] mb-4 text-center" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Demandez votre devis gratuit à {page.city}
            </h2>
            <p className="text-[#444] text-center mb-8">Tous travaux de construction et rénovation — Devis gratuit – réponse sous 48 h</p>
          </ScrollReveal>
          <ScrollReveal delay={200}><QuoteForm /></ScrollReveal>
        </div>
      </section>

      {/* 6. Schema.org */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org", "@type": "GeneralContractor", "name": "VGI Construct",
        "description": `Rénovation et travaux à ${page.city} — VGI Construct SRL, entreprise générale de rénovation depuis 2018`,
        "telephone": "+32466407988", "email": "info@vgiconstruct.be",
        "address": { "@type": "PostalAddress", "streetAddress": "Bergensesteenweg 24/8", "addressLocality": "Sint-Pieters-Leeuw", "postalCode": "1600", "addressCountry": "BE" },
        "areaServed": [
          { "@type": "City", "name": page.city },
          { "@type": "AdministrativeArea", "name": "Bruxelles-Capitale" },
          { "@type": "AdministrativeArea", "name": "Brabant flamand" },
          { "@type": "AdministrativeArea", "name": "Brabant wallon" }
        ],
        "openingHours": "Mo-Fr 08:00-18:00"
      })}} />
    </div>
  );
}
